import React, {createRef, useState, useContext, useEffect, useCallback, useRef} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { defaultComponents, MaterialTableBody, MaterialTableBodyRow } from '../../../controls/Material-Table-Components';
import {Checkbox, Tooltip, Toolbar, useTheme} from "@material-ui/core";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';

import SystemContext from "../../../SystemContext";

import AirportLookup from "../../Airports/components/AirportsLookup";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import CostingSectorsValidator from "../validators/CostingSectorsValidator";
import { MaskedTime, MaskedNumber, MaskedText, ComboSelect, ChromeTabs } from '../../../controls';
import Icons, { AirCargoIcon, AirCargoInverseIcon, HandshakeIcon } from '../../../icons';
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import axios from 'axios';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { fetch as fetchCostingFuelRate } from '../../../services/costings/useCostingFuelRate';
import { fetch as fetchFlightJetPlan } from '../../../services/jeppesen/useFlightJetPlans';
import FuelSupplierLookup from '../../Companies/components/FuelSupplierLookup';
import { runSectorJetPlan } from '../includes/costingDialogFunctions';
import { ReactComponent as ChecklistStatusIndicator } from '../../../assets/ChecklistStatusIndicator.svg';
import { useCostingFuelRate } from '../../../services/costings';
import { viewJetplansPopup } from '../../Jeppesen/ViewJetplans';

const apiSitePrefix = getApiSitePrefix();

const formatChars={
  "U": "[A-Za-z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "n": "-",
  "x": "."
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const StatusIndicator = (props) => {
  const {color} = props;
  if (color === "none") {
    return null;
  }
  return (    
    <div style={{position: "absolute", top: -10, right: -8}}>
      <div>
        <ChecklistStatusIndicator style={{fill: `${color||'#ff0000'}`, stroke: "#777777", width: 10, height: 10 }}/>
      </div>
    </div>
  )
}

const WaitForJetplan = (props) => {

  const {eventBus, queryClient, costingSectorUuid, status} = props;

  const planCheckTimeoutId = useRef(null);
  useEffect(()=> {
    let checkCount = 0;
    const checkForPlan = () => {
      fetchFlightJetPlan({queryClient, extraKeys: [costingSectorUuid, 'FULL']}).then(
        (response) => {
          if (response && response.exists && response.status !== 'CALCULATING') { // foundplan 
            const optionRouteText = response?.jeppsParams?.trackCode;
            if (props.onChange) props.onChange({leg: {...response.planData, costingSectorUuid: response.tripLegUuid, jetPlanStatus: response.status, fuelPolicyType: response.status==='FAILED'?'FAILED':'SECTOR', optionRouteText, ...__.pick(response, ['flightJetPlanUuid'])}});            
          } else {
            checkCount++;
            planCheckTimeoutId.current = setTimeout(checkForPlan, checkCount>100?30000:checkCount<30?5000:10000);
          }    
        }
      ).catch(err => {
        console.log(err);
      })
    }
    if (!planCheckTimeoutId.current) {
      checkCount = 0;
      planCheckTimeoutId.current = setTimeout(checkForPlan, 10000);
    }
    return () => {
      if (planCheckTimeoutId.current) clearTimeout(planCheckTimeoutId.current);
      planCheckTimeoutId.current = null;
      checkCount = 0;
    }
  }, [costingSectorUuid, planCheckTimeoutId, props, queryClient])

  return (
    <div>Waiting for Jetplan</div>
  )
}

const CostingSectorsEditor = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();
  const queryClient = useQueryClient();
  const eventBus = SystemData.eventBus;
  const {record, readOnly} = props;
  const oldProps = __.cloneDeep(props);
  const {costingDate} = record;

  useEffect(()=> {
    const addSector = async () => {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
  }
    if (eventBus) {
      eventBus.bind('CostingSectorsEditor.addSector', addSector);    
      return () => {
        eventBus.unbind("CostingSectorsEditor.addSector", addSector);
      }
    }

  }, [eventBus, tableRef]);

  const {classes, defaultValues} = props;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const sectors = __.filter(record.legs, {transportType: 'PRIMARY'});
  const otherSectors = __.filter(record.legs, (leg)=> leg.transportType !== 'PRIMARY');

  const payloadHeaderStyle = { padding: "2px 2px", backgroundColor: "rgb(214, 214, 214)" };

  const viewJetplans = (rowData) => {
    const planData = rowData.planData;
    viewJetplansPopup(planData, `JetPlan for UCN-${record?.costingNumber}`, SystemData);
  }


  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "2px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(CostingSectorsValidator, false);
    const  lastAirport = Array.isArray(sectors) && sectors.length > 0?sectors[sectors.length - 1].destinationAirportCode:'';

    const getRowIndex = (rowData) => {
      if (Array.isArray(sectors) && sectors.length > 0) {
        let idx = __.findIndex(sectors, __.pick(rowData, ['originAirportCode', 'destinationAirportCode', 'transportType']));
        if (idx < 0) idx = sectors.length;
        return idx;
      } 
      return -1;
    }

    const getFuelTooltip = (originFuelRecord, costingDate) => {
      let style = { fontSize: "0.875rem", textAlign: "right" };
      let errorText = "";
      let tooltip = <></>;
      if (originFuelRecord.success) {
        const { supplierCode, validFrom, validTo } = originFuelRecord?.rateRecord||{};
        if (moment.utc(validTo).add(7, 'day').isBefore(moment.utc(costingDate || undefined))) {
          style['color'] = "#dd0044";
          errorText = "Rate expired more than 7 days ago";
        }
        tooltip = (
          <div>
            <div>
              <div style={{display: "inline-block", width: 50}}>Supplier:</div><div style={{display: "inline-block"}}><FuelSupplierLookup value={supplierCode} type="render"/></div>
            </div>
            <div>
              <div style={{display: "inline-block", width: 50}}>Validity:</div><div style={{display: "inline-block"}}>{moment(validFrom,'YYYY-MM-DD').format('DD MMM YYYY')} to {moment(validTo,'YYYY-MM-DD').format('DD MMM YYYY')}</div>
            </div>
            <div>
              {errorText}
            </div>
          </div>
        );
      } else {
        style['color'] = "#dd0000";
        if (originFuelRecord.error) {
          errorText = originFuelRecord.error || "No valid rate found";
        } else {
          errorText = "No valid rate found";
        }
        tooltip = (
          <div>
            {errorText}
          </div>
        );
      }
      return { tooltip, style, errorText };
    }

    const FuelTooltip = (props) => {
      const originAirportCode = props.originAirportCode;
      const originFuelRecord = useCostingFuelRate({queryClient, extraKeys:[originAirportCode], useCache: true});

      if (props.customFuel) {
        return (
          <Tooltip classes={{tooltip: classes.fuelTooltip}} title="The fuel price has been overridden!">
            <div style={{position: "relative"}}>
              <StatusIndicator color="#00FFFF"/>
              {props.children}
            </div>
          </Tooltip>
        );    
      }
      const {tooltip, style, errorText} = getFuelTooltip(originFuelRecord?.data||{success: false}, props.costingDate);
      return (
        <Tooltip classes={{tooltip: classes.fuelTooltip}} title={tooltip}>
          <div style={{position: "relative"}}>
            {errorText?<StatusIndicator color="#FF0000"/>:null}
            <div style={style}>{props.children}</div>
          </div>
        </Tooltip>
      );    
    }

    return [
      {...validatorColumn, resizable: false},
      {
        ...baseColumn,
        ...ColumnWidth(60,[5,2],undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        initialEditValue: lastAirport,
        editComponent: (props) => {
          let timeout = null;
          if (!props?.rowData?.originFuelRecord && !parseInt(props?.rowData?.fuelPrice) && (props?.rowData?.originAirportCode||'').length === 3) {
            const originAirportCode = props?.rowData?.originAirportCode;
            fetchCostingFuelRate({queryClient, extraKeys:[originAirportCode, costingDate], useCache: true}).then(
              (response) => {
                let originFuelRecord = {success: false, error: response.error};
                let fuelPrice = null;
                if (response && response.success && response.rateRecord) {
                  const {rate} = response.rateRecord;
                  fuelPrice = rate;
                  originFuelRecord = {...response.rateRecord, success: true};
                }
                if (timeout) clearTimeout(timeout);
                timeout = setTimeout(() => {
                  props.onRowDataChange({...props.rowData, fuelPrice, originFuelRecord, customFuel: false});                  
                }, 10);
            })
          }
          return (
            <AirportLookup
              value={props.value}
              returnType="object"
              onChange={(returnObject)=>{
                if (returnObject.found) {
                  const originAirportCode = returnObject.data.airportCode;
                  fetchCostingFuelRate({queryClient, extraKeys:[originAirportCode, costingDate], useCache: true}).then(
                    (response) => {
                      let originFuelRecord = {success: false, error: response.error};
                      let fuelPrice = null;
                      if (response && response.success && response.rateRecord) {
                        const {rate} = response.rateRecord;
                        fuelPrice = rate;
                        originFuelRecord = {...response.rateRecord, success: true};
                      }
                    props.onRowDataChange({...props.rowData, originAirportCode, fuelPrice, originFuelRecord, customFuel: false});
                  })                  
                } else {
                  props.onRowDataChange({...props.rowData, originAirportCode: returnObject.value, fuelPrice: null, originFuelRecord:{success: false, error: "Invalid Origin Airport"}});
                }
              }}
            />
          )
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Dest",
        field: "destinationAirportCode",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            returnType="object"
            onChange={(returnObject)=>{
              if (returnObject.found) {
                const data = {
                  ...props.rowData,
                  destinationAirportCode: returnObject.data.airportCode,
                  alternateAirportCode: returnObject.data.alternateAirportCode, 
                  alternateAirportIcaoCode: returnObject.data.alternateAirportIcaoCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Alt",
        field: "alternateAirportCode",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            returnType="object"
            onChange={(returnObject)=>{
              if (returnObject.found) {
                const data = {
                  ...props.rowData,
                  alternateAirportCode: returnObject.data.airportCode, 
                  alternateAirportIcaoCode: returnObject.data.icaoCode, 
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      // {
      //   ...baseColumn,
      //   ...ColumnWidth(110,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
      //   title: "Transport Type",
      //   field: "transportType",
      //   initialEditValue: 'PRIMARY',
      //   render: (rowData) => (
      //     <ComboSelect
      //       id="transportType"
      //       group="TRANSPORT TYPES"
      //       subGroup=""
      //       fullWidth
      //       value={`${rowData.transportType}`}
      //       displayOnly={true}
      //     />
      //   ),
      //   editComponent: (props) => {
      //     return (
      //     <ComboSelect
      //       id="transportType"
      //       group="TRANSPORT TYPES"
      //       subGroup=""
      //       fullWidth
      //       value={`${props.value}`}
      //       onChange={(transportType) => {
      //         let legType = props.rowData.legType||"FULL CHARTER";
      //         if (transportType === "PRIMARY") {
      //           if (legType === "NO FREESALE") {
      //             legType = "";
      //           }
      //         } else {
      //           if (legType !== "NO FREESALE" && legType !== "FREESALE") {
      //             legType = "";
      //           }
      //         }
      //         props.onRowDataChange({...props.rowData, transportType, legType});
      //       }}
      //     />
      //   )},
      // },
      {
        ...baseColumn,
        ...ColumnWidth(110,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Leg Type",
        field: "legType",
        initialEditValue: 'FULL CHARTER',
        render: (rowData) => (
          <ComboSelect
            id="legType"
            group="TRIP LEG TYPES"
            subGroup={"PRIMARY"}
            fullWidth
            value={rowData.legType}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
          <ComboSelect
            id="legType"
            group="TRIP LEG TYPES"
            subGroup={"PRIMARY"}
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )},
      },
      {
        ...baseColumn,
        ...ColumnWidth(1,2,undefined,{width: 6, minWidth: 6, maxWidth: 6},baseColumn.headerStyle,undefined,"NONE"),
        title: " ",
        field: "",
        render: ()=>(<></>),
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,payloadHeaderStyle,undefined,"NONE"),
        groupTitle: "Payload",
        title: "Remaining",
        field: "payloadRemaining",
        type: "numeric",
        editable: false,
        render: (rowData, rowType, updatedData ) => {
          if (getRowIndex(rowData||updatedData||{}) < 1) {
            return (<>--</>)
          }
          return (
            <MaskedNumber 
              displayType="text"
              defaultValue={0}
              value={rowData.payloadRemaining||0}
              decimalScale={0}
              fixedDecimalScale={true}
              valueCap={props.weightUom==="TON"?150:150000}
              suffix={props.weightUom==="TON"?" tons":" kg"}
              inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,payloadHeaderStyle,undefined,false),
        groupTitle: "Payload",
        title: "Uplifted",
        field: "payloadUp",
        type: "numeric",
        editable: false,
        render: (rowData) => (
          <MaskedNumber 
            displayType="text"
            defaultValue={rowData.payloadUp||0}
            value={rowData.payloadUp||0}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,payloadHeaderStyle,undefined,"NONE"),
        groupTitle: "Payload",
        title: "Onboard",
        field: "payloadOnboard",
        type: "numeric",
        editable: false,
        render: (rowData, rowType, updatedData ) => (
          <MaskedNumber 
            displayType="text"
            defaultValue={0}
            value={rowData.payloadOnboard}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,payloadHeaderStyle,undefined,false),
        groupTitle: "Payload",
        title: "Unloaded",
        field: "payloadOff",
        type: "numeric",
        editable: false,
        render: (rowData) => (
          <MaskedNumber 
            displayType="text"
            defaultValue={rowData.payloadOff}
            value={rowData.payloadOff}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={rowData.payloadOnboard}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,undefined,undefined,undefined,{textAlign: "center"}),
        title: "No Fuel",
        field: "noFuel",
        emptyValue: false,
        initialEditValue: false,
        type: "boolean",
        render: rowData =>
          <div style={{textAlign:"center"}}>
            <Checkbox
              checked={(rowData.noFuel === 1 || rowData.noFuel === true) ?? true}
              style={{padding: 0}}
            />
          </div>,
        editComponent: props =>
          <div style={{textAlign:"center"}}>
            <Checkbox
              checked={(props.rowData.noFuel === 1 || props.rowData.noFuel === true) ?? true}
              style={{padding: 0, textAlign:"center"}}
              onChange={ (event, checked) => {
                props.onChange(checked)
              }}
            />
          </div>,
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Fuel Price",
        field: "fuelPrice",
        type: "numeric",
        initialEditValue: '0',
        editable: (field, rowData, updated)=>{return props.canEdit && !(updated?updated.noFuel:rowData.noFuel)},
        render: (rowData) => {          
          return rowData.noFuel?null:(
            <FuelTooltip originAirportCode={rowData.originAirportCode} customFuel={rowData.customFuel} fuelPrice={rowData.fuelPrice} costingDate={record.custingDate}>
              <MaskedNumber 
                displayType="text"
                defaultValue={rowData.fuelPrice||0}
                value={rowData.fuelPrice||0}
                decimalScale={5}
                valueCap={99.99999}
                prefix="$"
                suffix="/usg"
                inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
              />
            </FuelTooltip>
        )},
        editComponent: (props) => { 
          const originFuelRecord = props.rowData.originFuelRecord||{success: false};
          // let { tooltip, style, errorText } = getFuelTooltip(originFuelRecord);
          return (
            // <Tooltip title={tooltip}>
            //   <div style={{position: "relative"}}>
            //     {errorText?
            //       <StatusIndicator color="#FF0000"/>
            //     :null}
                <MaskedNumber 
                  displayType="input"
                  defaultValue={props.value||0}
                  value={props.value}
                  decimalScale={5}
                  valueCap={99.99999}
                  prefix="$"
                  suffix="/usg"
                  inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
                  onChange={(newPrice) => {
                    console.log(newPrice, originFuelRecord.rate);
                    if (newPrice && newPrice !== originFuelRecord.rate) {
                      const data = {
                        ...props.rowData,
                        fuelPrice: newPrice,
                        customFuel: true,
                      };    
                      console.log(data); 
                      props.onRowDataChange(data);
                    } else {
                      const originAirportCode = props.rowData.originAirportCode;
                      if (originAirportCode) {
                        fetchCostingFuelRate({queryClient, extraKeys:[originAirportCode, costingDate], useCache: true}).then(
                          (response) => {
                            let originFuelRecord = {success: false, error: response.error};
                            let fuelPrice = null;
                            if (response && response.success && response.rateRecord) {
                              const {rate} = response.rateRecord;
                              fuelPrice = rate;
                              originFuelRecord = {...response.rateRecord, success: true};
                            }
                          props.onRowDataChange({...props.rowData, originAirportCode, fuelPrice, originFuelRecord, customFuel: false});
                        })                  
                      } else {
                        const data = {
                          ...props.rowData,
                          fuelPrice: newPrice,
                          customFuel: true,
                        };   
                        props.onRowDataChange(data);                          
                      }
                    }
                  }}
                  // style={style}
                />
            //   </div>
            // </Tooltip>
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(265,[10,2],undefined,undefined,{...baseColumn.headerStyle, paddingLeft: 10},undefined,false),
        title: "Fuel Policy",
        field: "fuelPolicyType",
        emptyValue: "",
        defaultValue: "",
        initialEditValue: "",
        editable: (rowData) => props.canEdit && rowData.flightJetPlanUuid && rowData.fuelPolicyType,
        render: (rowData) => {
          if (rowData.flightJetPlanUuid && rowData.fuelPolicyType) {
            return (
              <ComboSelect
                group="FUEL POLICY TYPE"
                fullWidth
                value={rowData.fuelPolicyType}
                displayOnly={true}
                style={{textOverflow: 'ellipsis', width: 310, whiteSpace: 'nowrap', overflow: 'hidden'}}
              />
            )
          }
          if (rowData.jetPlanStatus === 'FAILED') {
            return <div style={{color: '#FF0000'}}>Plan Failed</div>
          }
          return (<WaitForJetplan status={rowData.jetPlanStatus} costingSectorUuid={rowData.costingSectorUuid} queryClient={queryClient} eventBus={eventBus} onChange={props.onChange}/>);
        },
        editComponent: (props) => (
          <ComboSelect
            group="FUEL POLICY TYPE"
            fullWidth
            value={props.value}
            filter={(options) => {
              return __.filter(options, (option) => (defaultValues?.fuelPolicyTypes||[]).indexOf(option.code) >= 0);
            }}
          />
        )
      },
    ];
  };
  
  const onDragEnd = (dragProps) => {
    if (!dragProps) return;
    if (!dragProps.source || !dragProps.destination) return;
    if (dragProps.source.index === dragProps.source.destination) return;

    if (Array.isArray(sectors)) {
      let records = [...sectors];
      let sourceRecord = records.splice(dragProps.source.index,1);
      const part1 = [...records].slice(0,dragProps.destination.index);
      const part2 = [...records].slice(dragProps.destination.index);
      const newRecords = [...part1, ...sourceRecord, ...part2];
      sectors.splice(0, sectors.length, ...newRecords);
      props.onChange({record: {...props.record, legs: [...sectors, ...otherSectors]}, oldRecord: props.record});
    }
  }

  const bodyRef = createRef();

  return (isAuthenticated &&
      <div className={classes.costingSectorsEditor}>
        <SectionHeading>Primary Legs</SectionHeading>
        <MaterialTable
          tableRef={tableRef}
          data={sectors}
          style={{
            backgroundColor: "#fdfdfd",
            boxShadow: "none",
            border: "2px solid #aaaaaa",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            search: false,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.childTableHeader.backgroundColor,
              color: theme.palette.childTableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            detailPanelType: "single",
            showTitle: false,
            toolbar: false,
            padding: 'dense',
            paging: false,
            filtering: false,
            draggable: false,
          }}
          components={{
            ...defaultComponents,
            Body: (props) => (
              <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId={"singleColumnDroppableAreaThusStaticInput"}
              >
                {(provided) => (
                  <MaterialTableBody
                    {...props}
                    ref={bodyRef} 
                    forwardedRef={provided.innerRef} 
                    placeHolder={provided.placeholder}
                    rowHeight={39}
                    overscanRowCount={10}
                    addVirtualScrolling={true}
//                      bodyHeight={300}
                    extraEditHeight={46}
                    headerHeight={46}
                  />
                )}
              </Droppable>
            </DragDropContext>
            ),
            Row: (props) => (
              <Draggable
                draggableId={props.data.tableData.id.toString()}
                index={props.data.tableData.id}
              >
                {(provided) => {
                  return (
                    <MaterialTableBodyRow
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}  
                      {...props}
                      forwardedRef={provided.innerRef}
                    />
                  );
                }}
              </Draggable>
            ),
          }}
          actions={[
            (rowData) => {
              return (
              {
                hidden: false,
                icon: ()=>(
                  <div style={{position: "relative"}}>
                  {rowData.optionRouteText === 'J' || (__.has(rowData.planData, 'planText') && __.has(rowData.planData.planText, 'error'))?
                    <StatusIndicator color="#FF0000"/>
                  :null}  
                    <Icons style={{color: __.has(rowData.planData, 'planText')?__.has(rowData.planData.planText, 'error') || rowData.optionRouteText === 'J'?'#FF0000':theme.palette.primary.primary:'#aaaaaa', marginTop: 3}}>{rowData?.planData?.planText?.error?"error_outline":"RouteOndIcon"}</Icons>
                  </div>
                ),
                tooltip: (__.has(rowData.planData, 'planText') && __.has(rowData.planData.planText, 'error'))?(<div style={{fontSize: 12}}><div>View Failed Plan(s)</div></div>):rowData.optionRouteText === 'J'?(<div style={{fontSize: 12}}><div>View Plan(s)</div><div style={{backgroundColor: "#ff0000", color: '#ffffff'}}>Using Optimised Plan</div></div>):(<div style={{fontSize: 12}}>View Database Plan(s) - {rowData.optionRouteText}</div>),
                isFreeAction: false,
                disabled: !__.has(rowData.planData, 'planText'),
                onClick: (e, rowData) => {
                  if (__.has(rowData.planData, 'planText')) viewJetplans(rowData);
                }
              }
              )
            },
          ]}
          title="Costing Sectors"
          columns={getColumns({...props, canEdit: state.canEdit})}
          editable={{
            isEditable: () => {
              return state.canEdit && !readOnly;
            },
            isDeletable: () => {
              return state.canEdit && !readOnly;
            },
            onRowAdd: state.canEdit && !readOnly
              ? (newData) => new Promise((resolve, reject) => {
                if (Array.isArray(sectors)) {
                  const newSector = {...newData, transportType: 'PRIMARY', costingSectorUuid: uuidv4()};
                  runSectorJetPlan(props.record, newSector, apiSitePrefix).finally((response) => {
                    sectors.push({...newSector, fuelPolicyType: '', jetPlanStatus: ''});
                    props.onChange({legs: [...sectors, ...otherSectors], resolve, reject});  
                  });
                } else {
                  reject();
                }
              })
              : undefined,
            onRowUpdate: state.canEdit && !readOnly
            ?(newData, oldData) =>
              new Promise((resolve, reject) => {
                const sectorIdx = __.findIndex(sectors, (trip) => {
                  const tripData = __.omit(trip, ["tableData"]);
                  const oldRecord = __.omit(oldData, ["tableData"]);
                  return __.isEqual(tripData, oldRecord);
                }); //sectors.indexOf(oldData);  
                if (sectorIdx >= 0) {
                  runSectorJetPlan(props.record, newData, apiSitePrefix).finally((response) => {
                    sectors[sectorIdx] = {...newData, fuelPolicyType: '', jetPlanStatus: ''};
                    props.onChange({legs: [...sectors, ...otherSectors], resolve, reject});  
                  })
                } else {
                  reject();
                }
              })
              : null,
            onRowDelete: state.canEdit && !readOnly
            ?(oldData) =>
              new Promise((resolve, reject) => {
                const sectorIdx = __.findIndex(sectors, (trip) => {
                  const tripData = __.omit(trip, ["tableData"]);
                  const oldRecord = __.omit(oldData, ["tableData"]);
                  return __.isEqual(tripData, oldRecord);
                }); //sectors.indexOf(oldData);
                if (sectorIdx >= 0) {
                  sectors.splice(sectorIdx, 1);
                  props.onChange({legs: [...sectors, ...otherSectors], resolve, reject});
                } else {
                  reject();
                }
              })
              :null,
          }}
        />
      </div>
  );
}

export default CostingSectorsEditor;


